import { useState, useEffect, useRef } from 'react';
import { FetchAssets } from '../api/assetService';
import { FetchCategoriesCustomAttributes } from '../api/assetCategoriesCustomAttributesService';

import { useAssetMetadata } from './useAssetMetadata';

/**
 * Hook for fetching and managing asset data. Combines asset metadata with
 * custom properties and provides progress updates while fetching paginated
 * results. Fetching is triggered by changes in `reloadFlag` or search options.
 *
 * @param {string} selectedProjectId ACC project ID
 * @param {string} selectedCategoryId Selected asset category ID
 * @param {string} assetFetchOptions Additional query string parameters
 * @param {*} reloadFlag Value toggled to trigger a reload
 * @param {Function} [onProgress] Progress callback
 * @returns {Object} Hook result
 */

export const useAssets = (selectedProjectId, selectedCategoryId, assetFetchOptions = '', reloadFlag, onProgress = null, getAccessToken) => {
    // STATE MANAGEMENT
    // Core asset data array - contains processed assets with display names instead of IDs
    const [data, setData] = useState([]);
    
    // OPTIONS MAPPINGS - These map IDs to display names for dropdowns and filters
    const [statusOptions, setStatusOptions] = useState({}); // {statusId: "Active", statusId2: "Inactive"}
    const [categoryOptions, setCategoryOptions] = useState({}); // {categoryId: "Equipment", categoryId2: "Furniture"}
    const [customPropsMap, setCustomPropsMap] = useState({}); // {fieldName: "Display Name"}
    const [locationsMap, setLocationsMap] = useState({}); // {locationId: "Building A", locationId2: "Building B"}
    
    // CUSTOM ATTRIBUTES FOR CATEGORIES
    const [categoryCustomAttributes, setCategoryCustomAttributes] = useState({}); // {categoryId: [customAttributes]}
    
    // LOADING AND ERROR STATES
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState({ currentCount: 0, hasMore: false });
    const [abortController, setAbortController] = useState(null);
    const canCancel = !!abortController; // Boolean flag for UI to show cancel option
    
    // REFS FOR STABLE REFERENCES
    // Store current option mappings in refs to avoid stale closures during async operations
    // This ensures data processing always uses the latest mappings without triggering re-renders
    const currentOptionsRef = useRef({ categoryOptions: {}, statusOptions: {}, locationsMap: {} });
    const currentParamsRef = useRef({ selectedProjectId, selectedCategoryId, assetFetchOptions });

    // CLEANUP EFFECT - Clear data when project or category changes
    // This prevents showing stale data from previous selections
    useEffect(() => {
        setData([]);
        setError(null);
    }, [selectedProjectId, selectedCategoryId]);

    // METADATA FETCHING
    // Fetch all metadata (categories, statuses, custom properties, locations) for the project
    // This provides the mapping data needed to convert IDs to display names
    // Pass reloadFlag to ensure metadata is refreshed when "Get from Server" is clicked
    const { categories, statuses, customProperties, locations, isLoading: metadataLoading, error: metadataError } = useAssetMetadata(selectedProjectId, selectedCategoryId, getAccessToken, reloadFlag);

    // METADATA PROCESSING EFFECT
    // Convert metadata arrays into lookup objects for efficient ID-to-name mapping
    // This runs whenever metadata is fetched/updated and creates the mappings used for:
    // 1. Data transformation (replacing IDs with display names in asset data)
    // 2. Dropdown options (for filters and editors)
    // 3. Display formatting (showing user-friendly names instead of IDs)
    useEffect(() => {
        // CATEGORY OPTIONS MAPPING
        // Transform: [{id: "cat1", name: "Equipment"}] -> {"cat1": "Equipment"}
        if (categories.length > 0) {
            const fetchedCategoryOptions = categories.reduce((acc, category) => {
                acc[category.id] = category.name;
                return acc;
            }, {});
            setCategoryOptions(fetchedCategoryOptions);
            currentOptionsRef.current.categoryOptions = fetchedCategoryOptions;
        }

        // STATUS OPTIONS MAPPING  
        // Transform: [{id: "status1", label: "Active"}] -> {"status1": "Active"}
        if (statuses.length > 0) {
            const fetchedStatusOptions = statuses.reduce((acc, status) => {
                acc[status.id] = status.label;
                return acc;
            }, {});
            setStatusOptions(fetchedStatusOptions);
            currentOptionsRef.current.statusOptions = fetchedStatusOptions;
        }

        // CUSTOM PROPERTIES MAPPING
        // Transform: [{name: "warranty_date", displayName: "Warranty Date"}] -> {"warranty_date": "Warranty Date"}
        if (customProperties.length > 0) {
            const newCustomPropsMap = customProperties.reduce((acc, prop) => {
                acc[prop.name] = prop.displayName;
                return acc;
            }, {});
            setCustomPropsMap(newCustomPropsMap);
        }
        
        // LOCATIONS MAPPING
        // Transform: [{id: "loc1", name: "Building A"}] -> {"loc1": "Building A"}
        if (locations.length > 0) {
            const newLocationsMap = locations.reduce((acc, location) => {
                acc[location.id] = location.name;
                return acc;
            }, {});
            setLocationsMap(newLocationsMap);
            currentOptionsRef.current.locationsMap = newLocationsMap;
        }
    }, [categories, statuses, customProperties, locations]);

    // CUSTOM ATTRIBUTES FOR CATEGORIES FETCHING
    // Fetch custom attributes for each category to determine which fields have definitions
    useEffect(() => {
        if (!selectedProjectId || selectedProjectId.trim() === '' || categories.length === 0) {
            return;
        }

        const fetchCategoryCustomAttributes = async () => {
            try {
                const accessToken = await getAccessToken();
                const customAttributesMap = {};

                // Fetch custom attributes for each category
                for (const category of categories) {
                    try {
                        const response = await FetchCategoriesCustomAttributes({
                            selectedProjectId,
                            categoryId: category.id,
                            accessToken
                        });
                        
                        customAttributesMap[category.id] = response.results || [];
                        // console.log(`Custom attributes for category ${category.name} (${category.id}):`, response.results);
                    } catch (error) {
                        // console.error(`Error fetching custom attributes for category ${category.id}:`, error);
                        customAttributesMap[category.id] = [];
                    }
                }

                setCategoryCustomAttributes(customAttributesMap);
                // console.log('All category custom attributes fetched:', customAttributesMap);
            } catch (error) {
                console.error('Error fetching category custom attributes:', error);
            }
        };

        fetchCategoryCustomAttributes();
    }, [selectedProjectId, categories, getAccessToken]);

    // PARAMETER TRACKING EFFECT
    // Keep refs updated with current parameters to avoid stale closures in async operations
    useEffect(() => {
        currentParamsRef.current = { selectedProjectId, selectedCategoryId, assetFetchOptions };
    }, [selectedProjectId, selectedCategoryId, assetFetchOptions]);

    // MAIN DATA FETCHING EFFECT
    // Triggers when reloadFlag changes, search parameters change, or metadata becomes available
    // This is the core effect that fetches and processes asset data
    useEffect(() => {
        // EARLY RETURNS - Skip fetch if prerequisites aren't met
        if (!selectedProjectId || selectedProjectId.trim() === '') {
            console.log('No projectId available yet');
            return;
        }

        // Wait for metadata to load before fetching assets (we need the mappings)
        if (metadataLoading || categories.length === 0) {
            return;
        }

        const fetchData = async () => {
            // ABORT CONTROLLER SETUP
            // Create new AbortController for this fetch operation to allow cancellation
            const controller = new AbortController();
            setAbortController(controller);

            try {
                // AUTHENTICATION AND PARAMETERS
                const accessToken = await getAccessToken();
                const params = {
                    selectedProjectId,
                    selectedCategoryId,
                    accessToken
                };
                
                // LOADING STATE SETUP
                setIsLoading(true);
                setProgress({ currentCount: 0, hasMore: false });
                console.log('asset fetch options', assetFetchOptions);

                // PROGRESS CALLBACK
                const handleProgress = (progressInfo) => {
                    setProgress(progressInfo);
                    if (onProgress) {
                        onProgress(progressInfo);
                    }
                };

                // API CALL
                const tableDataResponse = await FetchAssets(params, assetFetchOptions, handleProgress, controller.signal);

                // DATA PROCESSING AND TRANSFORMATION
                // This is the critical step where raw API data gets transformed for UI consumption
                const processedData = tableDataResponse && tableDataResponse.results ? tableDataResponse.results.map(item => ({
                    // Spread original item properties
                    ...item,
                    // Flatten custom attributes to top level (e.g., warranty_date becomes a direct property)
                    ...item.customAttributes,
                    // CRITICAL: Replace ID values with display names for dropdown filters to work
                    // Raw data: {categoryId: "cat123"} -> Processed: {categoryId: "Equipment"}
                    // This transformation is essential for:
                    // 1. Dropdown filters matching correctly (filter looks for "Equipment", not "cat123")
                    // 2. User-friendly display in the table
                    // 3. Consistent data format across the application
                    categoryId: currentOptionsRef.current.categoryOptions[item.categoryId] || item.categoryId,
                    statusId: currentOptionsRef.current.statusOptions[item.statusId] || item.statusId,
                    locationId: currentOptionsRef.current.locationsMap[item.locationId] || item.locationId
                })) : [];

                // SUCCESS STATE UPDATE
                setData(processedData);
                setIsLoading(false);
                setProgress({ currentCount: processedData.length, hasMore: false });
                setAbortController(null);
            } catch (err) {
                // ERROR HANDLING
                if (err.name === 'AbortError' || err.message === 'Operation cancelled by user') {
                    console.log('Asset fetch was cancelled by user');
                    setError('Operation cancelled');
                } else {
                    console.error('Error during fetch operations:', err);
                    setError(err.message);
                }
                setIsLoading(false);
                setProgress({ currentCount: 0, hasMore: false });
                setAbortController(null);
            }
        };

        fetchData();
        // TODO not sure when automatic load of assets should happen
        // set here the automatic loading maybe assetFetchOptions, metadataLoading, categories
    }, [reloadFlag]);

    // CANCELLATION FUNCTION
    // Allows external components to cancel ongoing fetch operations
    // Useful for long-running requests that user wants to stop
    const cancelFetch = () => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setIsLoading(false);
            setProgress({ currentCount: 0, hasMore: false });
            setError('Operation cancelled by user');
        }
    };

    // HOOK RETURN VALUE
    // Provides all necessary data and functions for asset management
    return {
        data,                    // Processed asset array with display names instead of IDs
        setData,                // Function to update data externally (for adding individual assets)
        isLoading,              // Boolean indicating if fetch is in progress
        error: error || metadataError, // Any error from asset fetch or metadata fetch
        statusOptions,          // {statusId: "displayName"} mapping for dropdowns
        categoryOptions,        // {categoryId: "displayName"} mapping for dropdowns  
        customPropsMap,         // {fieldName: "displayName"} mapping for custom properties
        locationsMap,           // {locationId: "displayName"} mapping for dropdowns
        categoryCustomAttributes, // {categoryId: [customAttributes]} mapping for category-specific custom attributes
        progress,               // {currentCount: number, hasMore: boolean} for progress UI
        cancelFetch,            // Function to cancel ongoing fetch operation
        canCancel               // Boolean indicating if cancellation is possible
    };
};
